import * as moment from "moment";

import axios, { AxiosError, AxiosResponse } from "axios";
import { format } from "date-fns";

import Utils, { ResponseWrapper, ServerModelWithDateStrings } from "@teamprevent/common/utils";
import { NonFixedLengthChildAppointment, mapServerModelToAppointmentModel } from "@teamprevent/common/calendar/models";

import { caseTypeOptions } from "../../healthSurveillance";

import {
    AddUnknownEmployeeModel,
    BlockClinicModel,
    BlockClinicResult,
    ClinicAndDistance,
    ClinicAndDistanceFailedGeocodeResult,
    ClinicAndDistanceResult,
    ClinicCreateModel,
    ClinicDetails,
    ClinicErrors,
    ClinicLocationType,
    ClinicType,
    CloneClinicRequest,
    CloneClinicResult,
    EditClinicCliniciansErrors,
    EditClinicModel,
    EditClinicResult,
    HealthSurveillanceCaseInfoModel,
    MatchingEmployeeModel,
    MobileClinicCreateModel,
    MobileClinicDetails
} from "./models";

class ClinicApi {
    private readonly apiUrl = "/api/clinic";
    private getInDateRangeWithFiltersAbortController = new AbortController();
    private getDistancesInDateRangeWithFiltersAbortController = new AbortController();

    public async getInDateRangeWithFilters(
        startDate: moment.Moment,
        endDate: moment.Moment,
        onlyInsufficientClinicians: boolean,
        clinicType: string,
        clinicLocationType: string,
        teamIds: string[],
        customerIds: string[],
        professionIds: string[],
        restrictToAppointmentId?: string
    ): Promise<ClinicDetails[]> {
        const startDateString = startDate.format(moment.HTML5_FMT.DATETIME_LOCAL);
        const endDateString = endDate.format(moment.HTML5_FMT.DATETIME_LOCAL);

        const params = {
            startDate: startDateString,
            endDate: endDateString,
            onlyInsufficientClinicians,
            clinicType,
            clinicLocationType,
            teamIds,
            customerIds,
            professionIds,
            restrictToAppointmentId
        };

        this.getInDateRangeWithFiltersAbortController.abort("Cancelled to allow subsequent request");
        this.getInDateRangeWithFiltersAbortController = new AbortController();

        return (await axios.get(this.apiUrl, {
            params,
            signal: this.getInDateRangeWithFiltersAbortController.signal
        })).data as ClinicDetails[];

    }

    public async get(clinicId: string): Promise<ClinicDetails> {
        return (await axios.get(`${this.apiUrl}/${clinicId}`)).data as ClinicDetails;
    }
    public async getClinicDetails(clinicId: string): Promise<any> {
        return (await axios.get(`${this.apiUrl}/${clinicId}`)).data;
    }

    public async getDistancesInDateRangeWithFilters(
        startDate: moment.Moment,
        endDate: moment.Moment,
        onlyInsufficientClinicians: boolean,
        clinicType: string,
        clinicLocationType: string,
        postcode: string,
        distance: number
    ): Promise<ClinicAndDistanceResult | ClinicAndDistanceFailedGeocodeResult> {
        const startDateString = startDate.format(moment.HTML5_FMT.DATETIME_LOCAL);
        const endDateString = endDate.format(moment.HTML5_FMT.DATETIME_LOCAL);

        const params = { startDate: startDateString, endDate: endDateString, onlyInsufficientClinicians, clinicType, clinicLocationType, postcode, distance };
        this.getDistancesInDateRangeWithFiltersAbortController.abort("Cancelled to allow subsequent request");
        this.getDistancesInDateRangeWithFiltersAbortController = new AbortController();
        try {
            const response = await axios.get(`${this.apiUrl}/distances`, { params, signal: this.getDistancesInDateRangeWithFiltersAbortController.signal });

            return {
                clinicsAndDistances: response.data as ClinicAndDistance[],
                geocodedSuccessful: true
            };
        } catch (ex) {
            const axiosError = ex as AxiosError;
            if (axiosError && axiosError.response && axiosError.response.status === 404) {
                return {
                    geocodedSuccessful: false
                };
            }

            throw ex;
        }
    }

    public async getClinicTypes(): Promise<ClinicType[]> {
        return (await axios.get(`${this.apiUrl}/types`)).data;
    }

    public async getClinicLocationTypes(): Promise<ClinicLocationType[]> {
        return (await axios.get(`${this.apiUrl}/locationtypes`)).data;
    }

    public async createClinic(clinic: ClinicCreateModel): Promise<ClinicDetails | ClinicErrors> {
        const { room, teamPreventUsers, clinicType, clinicLocationTypes, customers, billableByType, billableCustomers, ...createModel } = clinic;
        const mappedModel = {
            ...createModel,
            clinicType: clinicType.name,
            clinicLocationTypes: clinicLocationTypes.map(locationType => locationType.name),
            roomId: room && room.id,
            teamPreventUserIds: teamPreventUsers.map(user => user.id),
            start: clinic.start.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS),
            end: clinic.end.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS),
            customerIds: customers.map(customer => customer.id),
            billableByType: billableByType.name,
            billableCustomerIds: billableCustomers.map(bc => bc.id)
        };
        try {
            return (await axios.post(this.apiUrl, mappedModel)).data;
        } catch (ex) {
            const axiosError = ex as AxiosError<{ roomId: string } & Record<string, string>>;
            if (axiosError.response && axiosError.response.status && axiosError.response.status === 400) {
                const { roomId, ...userClashIds } = axiosError.response.data;
                return { errors: { roomId, userClashIds: Object.keys(userClashIds) } };
            }

            throw axiosError;
        }
    }

    public async createMobileClinic(clinic: MobileClinicCreateModel): Promise<MobileClinicDetails | ClinicErrors> {
        const { teamPreventUsers, ...createModel } = clinic;
        const mappedModel = {
            ...createModel,
            teamPreventUserIds: teamPreventUsers.map(user => user.id),
            start: clinic.start.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS),
            end: clinic.end.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS)
        };
        try {
            return (await axios.post(`${this.apiUrl}/mobile-clinic`, mappedModel)).data;
        } catch (ex) {
            const axiosError = ex as AxiosError<{ roomId: string } & Record<string, string>>;
            if (axiosError.response && axiosError.response.status && axiosError.response.status === 400) {
                const { roomId, ...userClashIds } = axiosError.response.data;
                return { errors: { userClashIds: Object.keys(userClashIds) } };
            }

            throw axiosError;
        }
    }

    public cloneClinic = async (cloneRequest: CloneClinicRequest): Promise<CloneClinicResult[]> => {
        const formattedDates = cloneRequest.dates.map(d => ({
            start: format(d.start, "yyyy-MM-dd'T'HH:mm:ss"),
            end: format(d.end, "yyyy-MM-dd'T'HH:mm:ss")
        }));
        const result = await axios.post<
            ServerModelWithDateStrings<CloneClinicResult>[],
            AxiosResponse<ServerModelWithDateStrings<CloneClinicResult>[]>,
            ServerModelWithDateStrings<CloneClinicRequest>
        >(`${this.apiUrl}/clone`, { ...cloneRequest, dates: formattedDates });
        return result.data.map(r => ({ ...r, date: new Date(r.date) }));
    };

    public async updateClinicClinicians(clinicId: string, clinicianIds: string[]): Promise<ClinicDetails | EditClinicCliniciansErrors> {
        try {
            return (await axios.post(`${this.apiUrl}/${clinicId}/clinicians`, clinicianIds)).data;
        } catch (ex) {
            const axiosError = ex as AxiosError<string[]>;
            if (axiosError.response && axiosError.response.status && axiosError.response.status === 400) {
                const userClashIds = axiosError.response.data;
                return { errors: { userClashIds } };
            }

            throw axiosError;
        }
    }

    public async searchForEmployee(clinicId: string, employeeName: string): Promise<MatchingEmployeeModel[]> {
        const response = (await axios.post(`${this.apiUrl}/${clinicId}/getEmployees`, {
            employeeName
        }));

        return response.data.map((employee: MatchingEmployeeModel) => ({
            ...employee,
            dateOfBirth: employee.dateOfBirth && moment(employee.dateOfBirth)
        }));
    }

    public async addUnknownEmployeeToClinic(clinicId: string, model: AddUnknownEmployeeModel): Promise<string> {
        try {
            return (await axios.post(`${this.apiUrl}/${clinicId}/unknownEmployee`, {
                ...model,
                healthSurveillanceCaseTypes: caseTypeOptions.find(o => o.value === model.caseTypes)?.values
            })).data;
        } catch (ex) {
            Utils.CheckException(ex);
            throw ex;
        }
    }

    public async editClinic(clinicId: string, model: EditClinicModel): Promise<EditClinicResult> {
        const parsedModel = {
            ...model,
            start: model.start.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS),
            end: model.end.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS),
            clinicLocationTypes: model.clinicLocationTypes.map(locationType => locationType.name),
        };
        const response = await axios.post(`${this.apiUrl}/${clinicId}/edit`, parsedModel);
        const result = Utils.CheckResponse<EditClinicResult>(response.data);
        return {
            clinic: result?.clinic as ClinicDetails,
            success: response.data.success
        };
    }

    public async blockClinic(clinicId: string, model: BlockClinicModel): Promise<BlockClinicResult> {
        const parsedModel = {
            ...model,
            start: model.start.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS),
            end: model.end.format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS)
        };
        const response = await axios.post<
            ResponseWrapper<BlockClinicResult>, AxiosResponse<ResponseWrapper<BlockClinicResult>>, ServerModelWithDateStrings<BlockClinicModel>
        >(`/api/appointment/timenote/${clinicId}/blockClinic`, parsedModel);
        const result = Utils.CheckResponse<BlockClinicResult>(response.data);
        return {
            clinic: result.clinic,
            success: response.data.success
        };
    }

    public async getEmployeeRecordRecallCases(customerId: string, employeeRecordId: string, clinicId: string): Promise<HealthSurveillanceCaseInfoModel[]> {
        try {
            const response = await axios.get<HealthSurveillanceCaseInfoModel[]>(
                `${this.apiUrl}/customer/${customerId}/employeeRecord/${employeeRecordId}/clinic/${clinicId}/recallCases`);
            return response.data.map(ohCase => ({
                ...ohCase,
                dueDate: ohCase.dueDate && moment(ohCase.dueDate)
            }));
        } catch (ex) {
            Utils.CheckException(ex);
            throw ex;
        }
    }

    public async addRecallCaseToMobileClinic(customerId: string, clinicId: string, employeeRecordId: string, ohCaseId: string) {
        const response = await axios.post<
            ServerModelWithDateStrings<NonFixedLengthChildAppointment>,
            AxiosResponse<ServerModelWithDateStrings<NonFixedLengthChildAppointment>>
        >(`${this.apiUrl}/customer/${customerId}/employeeRecord/${employeeRecordId}/clinic/${clinicId}/recallCases/${ohCaseId}`);
        return mapServerModelToAppointmentModel(response.data) as NonFixedLengthChildAppointment;
    }
}

export const clinicApi = new ClinicApi();
